import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../axios";
import { getS3URL } from "../../_helpers";
import { Loading } from "../Other/Loading";
import { addToCart, newAddToCart } from "../../_actions/cart.actions";
import toast from "react-hot-toast";
import { Sparkles, Tag, Percent, BadgeAlert, Check, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import logo from "../../assets/images/logo.png";
import "./OffersPage.scss";

// Stars rating renderer helper
const renderStars = (rating) => {
  const r = parseFloat(rating) || 5.0; // fallback to 5.0
  const stars = [];
  const fullStars = Math.floor(r);
  
  for (let i = 1; i <= 5; i++) {
    if (i <= fullStars) {
      stars.push(<span key={i} className="text-yellow-400">★</span>);
    } else {
      stars.push(<span key={i} className="text-slate-600">★</span>);
    }
  }
  
  return (
    <div className="flex items-center space-x-1">
      <span className="text-yellow-400 font-bold text-xs mr-1">{r.toFixed(1)}</span>
      <div className="flex text-xs tracking-tighter">{stars}</div>
    </div>
  );
};

// Blueprint Row Card Component
const OfferRowCard = ({ offer, onPurchase, purchaseLoading }) => {
  const sliderRef = useRef(null);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 350; // card width + gap
      sliderRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };

  const originalTotal = parseFloat(offer.totalAmount) || 0;
  const discountAmount = parseFloat(offer.discount) || 0;
  const finalPrice = parseFloat(offer.discountedAmount) || 0;

  return (
    <div className="offer-blueprint-row">
      {/* Left Checkout & Details Panel */}
      <div className="offer-left-details">
        <div className="category-header">
          <span className="tag-badge">
            <Tag size={12} className="mr-1" />
            {offer.category}
          </span>
          <span className="savings-badge">
            SAVE ₹{discountAmount.toFixed(0)}
          </span>
        </div>

        <h3 className="offer-row-heading">
          Looking to advance your skills in {offer.category || offer.title}? We've got you.
        </h3>
        <p className="offer-row-subheading">
          Get everything you need to reach your goals in one convenient bundle.
        </p>

        <ul className="offer-checklist">
          <li>
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            <span>Top-rated courses included</span>
          </li>
          <li>
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            <span>Popular with learners just like you</span>
          </li>
          <li>
            <Check size={14} className="text-emerald-500 mr-2 shrink-0" />
            <span>Guidance from real-world industry experts</span>
          </li>
        </ul>

        <div className="offer-pricing-summary">
          <span className="price-label">Total:</span>
          <div className="price-stack">
            <span className="original-price line-through text-slate-500 text-sm">₹{originalTotal.toFixed(2)}</span>
            <span className="discounted-price text-2xl font-extrabold text-slate-800 dark:text-white">
              ₹{finalPrice.toFixed(2)}
            </span>
          </div>
        </div>

        <button 
          onClick={() => onPurchase(offer)} 
          className="btn-add-all-cart"
          disabled={purchaseLoading}
        >
          {purchaseLoading ? "Processing..." : "Add all to cart"}
        </button>
      </div>

      {/* Right Horizontal Course Slider */}
      <div className="offer-right-slider-container">
        {offer.coursesData && offer.coursesData.length > 2 && (
          <div className="slider-controls">
            <button className="btn-arrow left" onClick={() => scroll("left")}>
              <ChevronLeft size={18} />
            </button>
            <button className="btn-arrow right" onClick={() => scroll("right")}>
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        <div className="courses-slider-track" ref={sliderRef}>
          {offer.coursesData?.map((course, idx) => {
            const courseImg = course?.imageFiles?.[0]?.fileName 
              ? getS3URL(course.imageFiles[0].fileName)
              : "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg";

            // Determine dynamic badges
            const ratingVal = parseFloat(course.rating) || 5.0;
            const showBestseller = ratingVal >= 4.8 || idx === 0;

            return (
              <React.Fragment key={course.ID}>
                {/* Linked '+' separator between courses in the bundle */}
                {idx > 0 && (
                  <div className="course-plus-separator">
                    <div className="plus-circle">
                      <Plus size={16} />
                    </div>
                  </div>
                )}

                <div className="course-slide-card">
                  <div className="card-image-wrap">
                    <img src={courseImg} alt={course.CourseName} />
                    <div className="card-badge-overlay">
                      {showBestseller && <span className="badge bestseller">Bestseller</span>}
                      <span className="badge premium">Premium</span>
                    </div>
                  </div>

                  <div className="card-body-details">
                    <h4 className="course-card-title">{course.CourseName}</h4>
                    <span className="course-card-instructor">{course.instructorName || "yslearn Expert"}</span>
                    
                    <div className="rating-row mt-2">
                      {renderStars(course.rating)}
                      <span className="text-xs text-slate-400">({Math.round((ratingVal * 15) % 100) + 12})</span>
                    </div>

                    <div className="price-row-footer">
                      <span className="price-label">Individual price</span>
                      <span className="price-val">₹{parseFloat(course.enrollmentFee || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main Offers Page
const OffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedIn, user, geoLocation } = useSelector((state) => state.authentication);
  const { CartItemsList } = useSelector((state) => state.cart);
  const userInfo = useSelector((state) => state.users?.user);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const [offersRes, categoriesRes] = await Promise.all([
        axiosInstance.get("/offers"),
        axiosInstance.get("/categories"),
      ]);

      if (offersRes?.data && !offersRes.data.error) {
        setOffers(offersRes.data.data);
      }
      if (categoriesRes?.data && !categoriesRes.data.error) {
        setCategories(categoriesRes.data.data);
      }
    } catch (err) {
      console.error("Error loading offers landing page:", err);
      toast.error("Failed to load offer packages.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (offer) => {
    setPurchaseLoading(true);
    try {
      if (loggedIn) {
        const resolvedUser = userInfo || user || {};
        const country = resolvedUser.country_code || "US";

        await dispatch(
          addToCart(
            { courses: offer.courses },
            `OFFER_${offer.id}`,
            geoLocation || {},
            false,
            offer.title
          )
        );
        toast.success("Offer bundle added to cart!");
        navigate("/cart");
      } else {
        // Offline user path
        const offerCourses = (offer.coursesData || []).map(course => ({
          ...course,
          PromoCodeApplied: `OFFER_${offer.id}`,
          coupon: `OFFER_${offer.id}`,
        }));

        const newArray = [...(CartItemsList || [])];
        offerCourses.forEach(course => {
          if (!newArray.some(obj => obj.ID === course.ID)) {
            newArray.push(course);
          }
        });

        dispatch(newAddToCart(newArray));
        toast.success("Offer bundle added to cart!");
        navigate("/cart");
      }
    } catch (err) {
      console.error("Error adding offer to cart:", err);
      toast.error("Failed to add offer to cart.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  const filteredOffers = selectedCategory === "All"
    ? offers
    : offers.filter(o => o.category === selectedCategory);

  if (loading) {
    return (
      <div className="offers-page-loader">
        <Loading />
        <p className="mt-3 text-slate-400 text-sm font-semibold">Configuring combined deals...</p>
      </div>
    );
  }

  return (
    <div className="public-offers-landing-page">
      <div className="background-decor-blobs">
        <div className="blob-1"></div>
        <div className="blob-2"></div>
      </div>

      <div className="offers-landing-container">
        {/* Banner Section */}
        <div className="offers-landing-banner">
          <div className="promo-pill">
            <Percent size={14} className="text-white" />
            <span>EXCLUSIVE VALUE BUNDLES</span>
          </div>
          <h1 className="banner-title">Combine Courses & Save Big</h1>
          <p className="banner-subtitle">
            Get unlimited access to structured path learning by bundling related courses. 
            Choose your focus, save with locked discounts, and kickstart your specialized track.
          </p>
        </div>

        {/* Categories Tab Selector */}
        <div className="categories-filter-bar">
          <button 
            className={`category-filter-tab ${selectedCategory === "All" ? "active" : ""}`}
            onClick={() => setSelectedCategory("All")}
          >
            All Offers
          </button>
          {(showAllCategories ? categories : categories.slice(0, 5)).map(cat => {
            const catName = cat.categoryName || cat.name;
            return (
              <button
                key={cat.id || cat.ID || catName}
                className={`category-filter-tab ${selectedCategory === catName ? "active" : ""}`}
                onClick={() => setSelectedCategory(catName)}
              >
                {catName}
              </button>
            );
          })}
          {categories.length > 5 && (
            <button
              className="category-filter-tab category-more-btn font-bold"
              onClick={() => setShowAllCategories(!showAllCategories)}
            >
              {showAllCategories ? "Less" : "More..."}
            </button>
          )}
        </div>

        {/* Offers Rows list */}
        {filteredOffers.length === 0 ? (
          <div className="empty-offers-view">
            <BadgeAlert size={48} className="text-indigo-400 mb-3" />
            <h3 className="font-extrabold text-lg text-slate-300">No offers currently active</h3>
            <p className="text-slate-400 text-sm mt-1">Please check back later or choose another category filter.</p>
          </div>
        ) : (
          <div className="offers-blueprint-list flex flex-col gap-12">
            {filteredOffers.map(offer => (
              <OfferRowCard 
                key={offer.id} 
                offer={offer} 
                onPurchase={handlePurchase}
                purchaseLoading={purchaseLoading}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersPage;
